// Bundelt de facturen van "GEFACTUREERDE VOLUME PER EAN" tot ÉÉN regel per
// aansluiting per klant.
//
// De tabel beantwoordt "hoeveel kWh is er op deze aansluiting gefactureerd". De
// losse factuurregels zijn daarvoor ruis — die staan al in DETAIL PER FACTUUR.
// Deze module staat apart omdat de sleutel niet vanzelfsprekend is en je het
// resultaat niet met het oog kan nakijken op tienduizenden facturen.
//
// Sleutel = energielabel · verkoopteam · verkoper · klant · EL GSRN · NG GSRN.
// Elk deel is nagemeten op de productiedata (56.466 facturen → 6.822 regels):
//
//  - **label/team/verkoper** — dezelfde afbakening als waarop de vergoeding
//    berekend wordt. 181 EL-GSRN's wisselden van verkoper en 67 van energielabel.
//    Die volumes samengooien levert een totaal op dat bij geen enkele
//    vergoedingsregel hoort.
//  - **klant** — 152 EL-GSRN's dragen meer dan één klant (verhuis/overname) en
//    107 facturen hebben helemaal géén GSRN, verdeeld over 15 klanten. Zonder de
//    klant in de sleutel vallen die op één hoop onder één regel.
//  - **EL en NG samen** — het koppel is stabiel: geen enkele EAN heeft een
//    wisselende tegenhanger. Dit splitst dus niets extra en houdt de regel in de
//    vorm die de tabel toont: EL links, NG rechts.

// Klantnummer als sleutel, met de naam als terugval — het nummer is stabiel, de
// naam kan van schrijfwijze veranderen.
export const eanKlantSleutel = (f) =>
  String(f.relatieReferentie || f.relatieNaam || '').trim().toLowerCase();

export const eanGroepSleutel = (f) =>
  [f.relatieBedrijf, f.verkoopteam, f.verkoper, eanKlantSleutel(f), f.elGSRN, f.ngGSRN]
    .map((v) => String(v || '').trim().toLowerCase()).join('|');

// Segment, majoration en meter method horen bij de AANSLUITING, maar zijn over de
// gebundelde facturen niet gegarandeerd constant (gemeten: majoration wisselt in
// 145 regels, meter method in 543). Daarom leveren we naast de waarde van de
// recentste factuur ook of ze varieert en welke waarden er voorkomen — de UI kan
// dat dan tonen in plaats van stil één waarde te kiezen die maar voor een deel van
// de facturen geldt.
export function eanAttribuut(facturen, lees) {
  const waarden = [];
  for (const f of facturen) {
    const v = lees(f);
    const s = v == null ? '' : String(v);
    if (s !== '' && !waarden.includes(s)) waarden.push(s);
  }
  return { waarde: lees(facturen[0]), varieert: waarden.length > 1, waarden };
}

const som = (facturen, lees) => facturen.reduce((s, f) => {
  const n = Number(lees(f));
  return s + (Number.isFinite(n) ? n : 0);
}, 0);

// `meterMethod` leest de meetmethode uit een factuur; die berekening zit in
// App.jsx (calcELDetail) en wordt daarom meegegeven i.p.v. hier herhaald.
export function groepeerEanVolumes(facturen, meterMethod = () => '') {
  const perSleutel = new Map();
  for (const f of facturen) {
    const k = eanGroepSleutel(f);
    if (!perSleutel.has(k)) perSleutel.set(k, []);
    perSleutel.get(k).push(f);
  }

  const uit = [];
  for (const [sleutel, groep] of perSleutel) {
    // Recentste factuur eerst: die levert de attribuutwaarden en de rij-identiteit.
    const gesorteerd = [...groep].sort((a, b) =>
      String(b.factuurdatum || '').localeCompare(String(a.factuurdatum || '')));
    const recentste = gesorteerd[0];
    uit.push({
      ...recentste,
      _sleutel: sleutel,
      aantalFacturen: gesorteerd.length,
      // De twee totaalkolommen tellen precies de facturen op die IN deze regel
      // zitten, zodat het getal altijd de regel beschrijft waar het op staat.
      // Geen GSRN aan die kant → geen totaal (leeg, niet 0: 0 zou "gemeten nul"
      // suggereren terwijl er simpelweg geen aansluiting is).
      totaalElKwh: recentste.elGSRN ? som(gesorteerd, (f) => f.elKwhOfftake) : null,
      totaalNgKwh: recentste.ngGSRN ? som(gesorteerd, (f) => f.ngKwhOfftake) : null,
      _elSegment: eanAttribuut(gesorteerd, (f) => f.elSegment),
      _ngSegment: eanAttribuut(gesorteerd, (f) => f.ngSegment),
      _elMaj: eanAttribuut(gesorteerd, (f) => f.elMajorationOfftake),
      _ngMaj: eanAttribuut(gesorteerd, (f) => f.ngMajorationOfftake),
      _meter: eanAttribuut(gesorteerd, meterMethod),
    });
  }
  return uit;
}
